import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'

const SERVER_AI_URL = import.meta.env.VITE_SERVER_AI_URL

// Connection states
export const SSEConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error'
}

const SSEContext = createContext(null)

export function SSEProvider({ children }) {
  const [connectionState, setConnectionState] = useState(SSEConnectionState.DISCONNECTED)
  const eventSourceRef = useRef(null)
  const subscribersRef = useRef(new Map())
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const subscriptionIdCounter = useRef(0)

  // Exponential backoff for reconnection
  const getReconnectDelay = useCallback(() => {
    const baseDelay = 1000 // 1 second
    const maxDelay = 30000 // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, reconnectAttemptsRef.current), maxDelay)
    return delay
  }, [])

  // Connect to SSE server
  const connect = useCallback(() => {
    // Don't create multiple connections
    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      return
    }

    // Clean up existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    console.debug('🟠 SSEProvider: Connecting to AI server')
    setConnectionState(reconnectAttemptsRef.current === 0 
      ? SSEConnectionState.CONNECTING 
      : SSEConnectionState.RECONNECTING
    )

    try {
      const es = new EventSource(`${SERVER_AI_URL}/connect`, {
        withCredentials: true
      })

      es.addEventListener('open', () => {
        console.debug('🟢 SSEProvider: Connected to AI server')
        setConnectionState(SSEConnectionState.CONNECTED)
        reconnectAttemptsRef.current = 0
      })

      es.addEventListener('message', (event) => {
        try {
          const message = JSON.parse(event.data)
          
          // Broadcast to all subscribers
          subscribersRef.current.forEach((subscriber) => {
            const { filter, callback } = subscriber
            
            // If no filter or filter returns true, deliver the message
            if (!filter || filter(message)) {
              try {
                callback(message)
              } catch (error) {
                console.error('SSEProvider: Error in subscriber callback', error)
              }
            }
          })
        } catch (error) {
          console.error('SSEProvider: Error parsing message', error)
        }
      })

      es.addEventListener('error', (event) => {
        console.debug('🔴 SSEProvider: Connection error', event)
        setConnectionState(SSEConnectionState.ERROR)
        
        // Close the connection
        es.close()
        
        // Schedule reconnection
        reconnectAttemptsRef.current++
        const delay = getReconnectDelay()
        console.debug(`🟠 SSEProvider: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)
        
        reconnectTimeoutRef.current = setTimeout(() => {
          connect()
        }, delay)
      })

      eventSourceRef.current = es
    } catch (error) {
      console.error('SSEProvider: Error creating EventSource', error)
      setConnectionState(SSEConnectionState.ERROR)
      
      // Schedule reconnection
      reconnectAttemptsRef.current++
      const delay = getReconnectDelay()
      reconnectTimeoutRef.current = setTimeout(() => {
        connect()
      }, delay)
    }
  }, [getReconnectDelay])

  // Disconnect from SSE server
  const disconnect = useCallback(() => {
    console.debug('🟠 SSEProvider: Disconnecting from AI server')
    
    // Clear reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    // Close EventSource
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    
    setConnectionState(SSEConnectionState.DISCONNECTED)
    reconnectAttemptsRef.current = 0
  }, [])

  // Subscribe to messages with optional filter
  const subscribe = useCallback((filter, callback) => {
    const id = subscriptionIdCounter.current++
    subscribersRef.current.set(id, { filter, callback })
    
    console.debug(`🟠 SSEProvider: Subscriber ${id} added (total: ${subscribersRef.current.size})`)
    
    // Return unsubscribe function
    return () => {
      subscribersRef.current.delete(id)
      console.debug(`🟠 SSEProvider: Subscriber ${id} removed (remaining: ${subscribersRef.current.size})`)
    }
  }, [])

  // Initialize connection on mount
  useEffect(() => {
    connect()
    
    // Cleanup on unmount
    return () => {
      disconnect()
    }
  }, [connect, disconnect])

  const value = {
    connectionState,
    subscribe,
    reconnect: connect,
    disconnect
  }

  return (
    <SSEContext.Provider value={value}>
      {children}
    </SSEContext.Provider>
  )
}

/**
 * Hook to subscribe to SSE messages
 * 
 * @param {Function} filter - Optional filter function to receive only specific messages
 *                            Example: (msg) => msg.botId === '123'
 * @param {Function} callback - Callback function to handle messages
 * 
 * @example
 * useSSE((msg) => msg.botId === bid, (message) => {
 *   console.log('Received message:', message)
 * })
 */
export function useSSE(filter, callback) {
  const context = useContext(SSEContext)
  
  if (!context) {
    throw new Error('useSSE must be used within SSEProvider')
  }

  const { subscribe } = context

  useEffect(() => {
    // Subscribe with filter and callback
    const unsubscribe = subscribe(filter, callback)
    
    // Cleanup subscription on unmount or when dependencies change
    return unsubscribe
  }, [subscribe, filter, callback])

  return context
}

/**
 * Hook to get SSE connection state
 * 
 * @returns {Object} { connectionState, reconnect, disconnect }
 */
export function useSSEConnection() {
  const context = useContext(SSEContext)
  
  if (!context) {
    throw new Error('useSSEConnection must be used within SSEProvider')
  }

  return {
    connectionState: context.connectionState,
    reconnect: context.reconnect,
    disconnect: context.disconnect
  }
}

